import { StockParentModelType } from '@teinor/erp/types/company/product/stockParent'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import ModalCreateEdit from '../../../../../../baseComponents/ModalsLayouts/ModalCreateEdit'
import { ModalEditComponentProps } from '../../../../../../baseComponents/ModalsLayouts/types'
import { FormRender } from '../../../../../../forms/FormRenderer/types'
import { OnSubmit } from '../../../../../../forms/TwinForm/types'
import { twinFetchPostJSON } from '../../../../../../utils/globals/data'
import { dictionaryComplexFromJsonArr } from '../../../../../../utils/globals/dictionary'
import useTwinTranslation from '../../../../../../utils/hooks/useTwinTranslation'
import { getActiveCurrenciesDictionary, getISOCurrencyMain } from '../../../../../../utils/reducers/getters'
import { changeErrorMessage } from '../../../../../../utils/reducers/reduxDispatch'
import { WarehousePayload } from '../../../../../../utils/reducers/company/warehouses'
import { connect, ConnectedProps } from 'react-redux'
import { AllReduxPayloads } from '../../../../../../utils/reducers'
import withLoading from '../../../../../../utils/hoc/withLoading'

type ModalCreateStockProductProps = ModalEditComponentProps & ReduxModalCreateStockProductConnector  & {}

const ModalCreateStockProduct: React.FC<ModalCreateStockProductProps> = ({ userPermissions, allRowData, warehouses, ...rest }) => {
    const { id, onError, t, selectedWarehouses, dictWarehouses, dictActiveCurrencies } = useModalCreateStockProductLogic({ warehouses })
    const mainCurrency = getISOCurrencyMain()
    const fields: FormRender<StockParentModelType> = [
        {
            cols: 2,
            elements: [
                {
                    name: 'ProductId',
                    component: 'InputHidden',
                    value: id,
                },
                {
                    name: 'WarehouseId',
                    label: t('warehouse', 'Almacén'),
                    component: 'CustomSelectSearchBarWithNotInFilteredItems',
                    items: dictWarehouses,
                    excludeItems: selectedWarehouses,
                    required: true,
                },
                {
                    name: 'CurrencyId',
                    label: t('currency', 'Moneda'),
                    component: 'CustomSelectWithSearchBar',
                    items: dictActiveCurrencies,
                    value: allRowData?.CurrencyId || mainCurrency,
                    required: true
                },
                {
                    name: 'location',
                    label: t('location', 'Localización'),
                    component: 'InputWithLabelMargin',
                    type: 'text',
                },
                {
                    name: 'minimum',
                    label: t('minimum', 'Mínimo'),
                    component: 'InputWithLabelMargin',
                    type: 'text',
                },
                {
                    name: 'optimum',
                    label: t('optimum', 'Óptimo'),
                    component: 'InputWithLabelMargin',
                    type: 'text',
                },
            ],
        },
    ]
    return (<ModalCreateEdit fields={fields} url={'/api/app/product/stock/createParentStock'} translations={{
        title: t('createStockOfProduct', 'Crear stock del producto'),
        button: t('create', 'Crear')
    }} haveButtonPermissions={true} {...rest} onChangeError={onError}  />)
}

interface ModalCreateStockProductLogicProps {
    warehouses: WarehousePayload
}

const useModalCreateStockProductLogic = ({ warehouses  }: ModalCreateStockProductLogicProps) => {
    const { t } = useTwinTranslation()
    const params = useParams<{ id: string }>()
    const id = params.id
    const [selectedWarehouses, setSelectedWarehouses] = useState<string[]>([])
    const errorMsg = t('checkProductExistsInWarehouse', 'Comprueba si el producto ya está en este Almacén')
    const dictWarehouses = useMemo(() =>dictionaryComplexFromJsonArr(warehouses || []), [warehouses])
    const dictActiveCurrencies = useMemo(() => getActiveCurrenciesDictionary() || {}, [])

    const getSelectedWarehouses = useCallback(async () => {
        const result = await twinFetchPostJSON('/api/app/product/stock/getAllStockParents', { where: { ProductId: id } })
        const selected: string[] = []
        for (const selectedWarehouse of result) {
            selected.push(String(selectedWarehouse.WarehouseId))
        }
        setSelectedWarehouses(selected)
    }, [setSelectedWarehouses, id])

    const onError: OnSubmit = useCallback(async (e, vals) => {
        changeErrorMessage(errorMsg)
    }, [errorMsg])

    useEffect(() => {
        getSelectedWarehouses()
    }, [getSelectedWarehouses])

    return { id, onError, t, selectedWarehouses, dictWarehouses, dictActiveCurrencies }
}

const modalCreateStockProductDispatch = {
    setWarehouses: (payload: WarehousePayload) => ({ type: 'CHANGE_WAREHOUSE', payload }),
}
const mapModalCreateStockProductConnector = (state: AllReduxPayloads) => ({ warehouses: state.warehouses })
const modalCreateStockProductConnector = connect(mapModalCreateStockProductConnector, modalCreateStockProductDispatch)
type ReduxModalCreateStockProductConnector = ConnectedProps<typeof modalCreateStockProductConnector>

const modalCreateStockProductConnectLoading = withLoading(ModalCreateStockProduct, [{ fetchUrl: '/api/app/warehouse/getAllWarehouses', propName: 'warehouses', setFunctionName: 'setWarehouses' }])

const ModalCreateStockProductConnect = modalCreateStockProductConnector(modalCreateStockProductConnectLoading)


export default ModalCreateStockProductConnect